import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Card,
  Checkbox,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import OutlinedInput from "@mui/material/OutlinedInput";
import KeyboardArrowRightRoundedIcon from "@mui/icons-material/KeyboardArrowRightRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import AttachFileRoundedIcon from "@mui/icons-material/AttachFileRounded";
import MailOutlineRoundedIcon from "@mui/icons-material/MailOutlineRounded";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { useQuery } from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import axios from "../../util/axios";
import { is } from "src/util/is";
import enums from "src/util/enums";
import { alertSlice } from "src/store/alert/alert.slice";
import { fetchWithError } from "src/react-query/fetchWithError";
import { queryKeys } from "src/react-query/queryKeys";
import { useAuth } from "src/hooks/useAuth.hook";
import { useSelectedTeam } from "src/hooks/useSelectedTeam";
import {
  getAllExistedTeam,
  payoutReportDownload,
  payoutReportEmail,
  payoutCommissionInvoice,
  payoutStatementPreview,
} from "src/react-query/endPoints";
import { downloadExcel } from "src/utils/downloadExcel";
import { generateTransactionPDFFromHTML } from "src/components/cmCustomer/TransactionPdf";
import InvoiceTemplate from "src/components/cmCustomer/InvoiceTemplate";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import EmailPayoutStatementDialog from "./EmailPayoutStatementDialog";
import { 
  MenuProps, 
  displayBiCode,
  normalizeInvoiceHtml,
  isAllSelected,
  payoutTableHeaderSx,
  payoutParentCellSx,
  payoutChildHeaderCellSx,
  payoutChildCellSx,
} from "./ReportTabList.constant";

const {
  actions: { setAlert },
} = alertSlice;

const PayoutReportTab = () => {
  const dispatch = useDispatch();
  const [payoutExpandedRows, setPayoutExpandedRows] = useState([]);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailRuns, setEmailRuns] = useState([]);
  const [downloadingId, setDownloadingId] = useState(null);

  const { user } = useAuth();
  const { teamId } = useSelectedTeam();
  const companyType = useMemo(() => user?.company?.companyType, [user]);
  const isCompanyLogin = user?.company?.id === teamId;

  const isCompanySelectAllRef = useRef(false);
  const isTeamSelectAllRef = useRef(false);

  // Commission-invoice PDF (HTML -> PDF, same flow as the transaction invoice).
  const invoiceRef = useRef(null);
  const [commissionInvoiceHtml, setCommissionInvoiceHtml] = useState(null);
  const [invoiceLoadingId, setInvoiceLoadingId] = useState(null);

  const [allTeamsData, setAllTeamsData] = useState([]);
  const [dummyTeamData, setDummyTeamData] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState([]);

  // Companies visible to a GP login (excludes the parent company itself).
  const companyOptions = useMemo(
    () => user?.role?.filter((team) => team.teamId !== user?.company?.id) || [],
    [user]
  );

  // Team dropdown source differs by login type:
  //  - GP  -> fetched teams (companyId / companyName)
  //  - P   -> the company's own teams from user.role (teamId / teamName)
  const teamOptions = companyType === "P" ? companyOptions : allTeamsData || [];
  const teamIdKey = companyType === "P" ? "teamId" : "companyId";
  const teamNameKey = companyType === "P" ? "teamName" : "companyName";

  // GP: fetch the full team list once, keep an untouched copy for re-filtering.
  useEffect(() => {
    const getAllTeam = async () => {
      try {
        const response = await axios.get(getAllExistedTeam(teamId));
        setAllTeamsData(response.data.data);
        setDummyTeamData(response.data.data);
      } catch (err) {
        console.log(err);
      }
    };
    if (companyType === "GP") getAllTeam();
  }, [companyType, teamId]);

  // Keep the Team dropdown scoped to the selected company / active team.
  useEffect(() => {
    if (isCompanyLogin) {
      setAllTeamsData(dummyTeamData);
    } else {
      setAllTeamsData(
        dummyTeamData?.filter((list) => list.parentCompanyId === teamId)
      );
    }
  }, [teamId, dummyTeamData]);

  const handleChangeCompany = (e) => {
    let value = e.target.value;
    if (Array.isArray(value)) value = value.filter((v) => v !== "all");
    setSelectedCompany(value);
    if (companyType === "GP" && value.length > 0) {
      setAllTeamsData(
        dummyTeamData.filter((filter) =>
          value.includes(filter.parentCompanyId)
        )
      );
    } else {
      setAllTeamsData(dummyTeamData);
      setSelectedTeam([]);
    }
  };

  const handleChangeTeam = (e) => {
    let value = e.target.value;
    if (Array.isArray(value)) value = value.filter((v) => v !== "all");
    setSelectedTeam(value);
  };

  // Server-side filters: which teams/companies to fetch payout runs for.
  const listParams = useMemo(() => {
    const params = {};
    if (selectedTeam.length > 0) params.teamFilter = selectedTeam;
    if (companyType === "GP" && selectedCompany.length > 0) {
      params.companyFilter = selectedCompany;
    }
    return params;
  }, [selectedTeam, selectedCompany, companyType]);

  const showTeamFilter =
    (companyType === "GP" || companyType === "P") && isCompanyLogin;

  const hasFilterSelected =
    selectedTeam.length > 0 || selectedCompany.length > 0;
  const canFetch =
    !is.empty(teamId) && (!showTeamFilter || hasFilterSelected);

  const { data: payoutData, isFetching: isPayoutFetching } = useQuery(
    queryKeys.payoutReportList(teamId, listParams),
    fetchWithError,
    {
      enabled: canFetch,
      retry: false,
    }
  );

  const payoutRows = useMemo(() => {
     if (Array.isArray(payoutData?.rows)) return payoutData.rows;
    return [];
  }, [payoutData]);

  const togglePayoutExpand = (id) => {
    setPayoutExpandedRows((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const openEmailDialog = (runs) => {
    setEmailRuns(runs);
    setEmailDialogOpen(true);
  };

  // Build a single-payout statement object from a run + one of its detail rows.
  const openEmailForPayout = (run, detail) => {
    openEmailDialog([
      {
        ...run,
        id: detail.payoutId,
        cpo: detail.beneficiary || run.cpo,
        total: detail.amount,
        netPayout: detail.amount,
        payees: 1,
        payments: "1 · 1 bank-matched",
      },
    ]);
  };

  // Build the statement preview: fetch the same HTML the email will send.
  // Throws on failure so the dialog can surface the error state.
  const handleBuildStatementPreview = async (runIds) => {
    const response = await axios.post(payoutStatementPreview(teamId), { runIds });
    return (
      response?.data?.data?.template_content ||
      response?.data?.template_content ||
      null
    );
  };

  // Send one payout statement email per run to the entered recipients.
  const handleSendPayoutStatement = async ({ emails, message, runIds }) => {
    // Throws on failure so the dialog can surface the error state.
    await axios.post(payoutReportEmail(teamId), { emails, message, runIds });
  };

  // Download a single payout's statement (calc sheet) as an Excel file.
  // payoutId is sent as `runIds` (array), e.g. { runIds: ["BI-977-29052026"] }.
  const handleDownloadPayoutStatement = async (payoutId) => {
    try {
      setDownloadingId(payoutId);
      const response = await axios.post(
        payoutReportDownload(teamId),
        { runIds: [payoutId] },
        { responseType: "blob" }
      );
      downloadExcel(response.data, `payout-statement-${payoutId}`);
    } catch (error) {
      dispatch(
        setAlert({
          open: true,
          message: "Failed to download the payout statement. Please try again.",
          severity: enums.SEVERITY.ERROR,
        })
      );
    } finally {
      setDownloadingId(null);
    }
  };

  // Download the Zynka commission invoice for a payout as a PDF.
  // Backend returns HTML (template_content); we render it hidden and rasterize
  // to PDF via the shared transaction-invoice flow.
  const handleDownloadCommissionInvoice = async (payoutId) => {
    if (invoiceLoadingId) return;
    try {
      setInvoiceLoadingId(payoutId);
      const response = await axios.post(
        payoutCommissionInvoice(teamId),
        { payoutId },
        { headers: { "Content-Type": "application/json" } }
      );
      // Endpoint returns { html, subject, invoiceNumber } (some return template_content).
      const content = response?.data?.html
      if (!content) {
        throw new Error("No invoice HTML returned");
      }
      // Reset first so InvoiceTemplate leaves its ready state; otherwise the PDF
      // helper can see the previous invoice's data-invoice-ready="true" and
      // rasterize stale DOM.
      setCommissionInvoiceHtml(null);
      await new Promise((r) => requestAnimationFrame(r));
      setCommissionInvoiceHtml(normalizeInvoiceHtml(content));
      await generateTransactionPDFFromHTML(invoiceRef, `commission-${payoutId}`);
    } catch (error) {
      dispatch(
        setAlert({
          open: true,
          message: "Failed to download the commission invoice. Please try again.",
          severity: enums.SEVERITY.ERROR,
        })
      );
    } finally {
      setInvoiceLoadingId(null);
    }
  };

  return (
    <Card sx={{ p: 0, overflow: "hidden" }}>
      {showTeamFilter && (
        <Grid
          container
          spacing={2}
          sx={{ px: 2.5, py: 2, borderBottom: "1px solid #E8EBF0" }}
        >
          {/* GP login: Company dropdown */}
          {companyType === "GP" && isCompanyLogin && (
            <Grid item lg={3} md={4} xs={6}>
              <FormControl size="small" sx={{ width: { xs: "100%", sm: "220px" } }}>
                <InputLabel id="payout-company-label">Select Company</InputLabel>
                <Select
                  labelId="payout-company-label"
                  multiple
                  value={selectedCompany}
                  onChange={(e) => {
                    if (isCompanySelectAllRef.current) {
                      isCompanySelectAllRef.current = false;
                      return;
                    }
                    handleChangeCompany(e);
                  }}
                  input={<OutlinedInput label="Select Company" />}
                  MenuProps={MenuProps}
                  renderValue={(selected) =>
                    companyOptions
                      .filter((team) => selected.includes(team.teamId))
                      .map((team) => team.teamName)
                      .join(", ")
                  }
                >
                  <MenuItem
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const allIds = companyOptions.map((team) => team.teamId);
                      const newValue = isAllSelected(companyOptions, selectedCompany)
                        ? []
                        : allIds;
                      isCompanySelectAllRef.current = true;
                      handleChangeCompany({ target: { value: newValue } });
                    }}
                  >
                    <Checkbox
                      checked={isAllSelected(companyOptions, selectedCompany)}
                      indeterminate={
                        selectedCompany.length > 0 &&
                        !isAllSelected(companyOptions, selectedCompany)
                      }
                    />
                    Select All
                  </MenuItem>
                  {companyOptions.map((team) => (
                    <MenuItem
                      key={team.teamId}
                      value={team.teamId}
                      onClick={() => {
                        const isSel = selectedCompany.indexOf(team.teamId) > -1;
                        const newSelected = isSel
                          ? selectedCompany.filter((id) => id !== team.teamId)
                          : [...selectedCompany, team.teamId];
                        handleChangeCompany({ target: { value: newSelected } });
                      }}
                    >
                      <Checkbox checked={selectedCompany.indexOf(team.teamId) > -1} />
                      {team.teamName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          {/* Team dropdown (GP + Company logins) */}
          <Grid item lg={3} md={4} xs={6}>
            <FormControl
              size="small"
              disabled={
                companyType === "GP" &&
                !(selectedCompany.length > 0 || !isCompanyLogin)
              }
              sx={{ width: { xs: "100%", sm: "220px" } }}
            >
              <InputLabel id="payout-team-label">Team Name</InputLabel>
              <Select
                labelId="payout-team-label"
                multiple
                value={selectedTeam}
                onChange={(e) => {
                  if (isTeamSelectAllRef.current) {
                    isTeamSelectAllRef.current = false;
                    return;
                  }
                  handleChangeTeam(e);
                }}
                input={<OutlinedInput label="Team Name" />}
                MenuProps={MenuProps}
                renderValue={(selected) =>
                  teamOptions
                    ?.filter((team) => selected.includes(team[teamIdKey]))
                    ?.map((team) => team[teamNameKey])
                    .join(", ")
                }
              >
                <MenuItem
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const allIds = teamOptions.map((team) => team[teamIdKey]);
                    const newValue = isAllSelected(teamOptions, selectedTeam)
                      ? []
                      : allIds;
                    isTeamSelectAllRef.current = true;
                    handleChangeTeam({ target: { value: newValue } });
                  }}
                >
                  <Checkbox
                    checked={isAllSelected(teamOptions, selectedTeam)}
                    indeterminate={
                      selectedTeam.length > 0 &&
                      !isAllSelected(teamOptions, selectedTeam)
                    }
                  />
                  Select All
                </MenuItem>
                {teamOptions.map((team) => (
                  <MenuItem
                    key={team[teamIdKey]}
                    value={team[teamIdKey]}
                    onClick={() => {
                      const id = team[teamIdKey];
                      const isSel = selectedTeam.indexOf(id) > -1;
                      const newSelected = isSel
                        ? selectedTeam.filter((v) => v !== id)
                        : [...selectedTeam, id];
                      handleChangeTeam({ target: { value: newSelected } });
                    }}
                  >
                    <Checkbox checked={selectedTeam.indexOf(team[teamIdKey]) > -1} />
                    {team[teamNameKey]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      )}
      <TableContainer sx={{ width: "100%", overflowX: "auto" }}>
        <Table sx={{ minWidth: 1260 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={payoutTableHeaderSx}>BI - CPO</TableCell>
              <TableCell sx={payoutTableHeaderSx}>Source</TableCell>
              <TableCell sx={payoutTableHeaderSx}>Period</TableCell>
              <TableCell sx={{ ...payoutTableHeaderSx, textAlign: "right" }}>
                Total ₹
              </TableCell>
              <TableCell sx={{ ...payoutTableHeaderSx, textAlign: "right" }}>
                Payees
              </TableCell>
              <TableCell sx={{ ...payoutTableHeaderSx, textAlign: "center" }}>
                Calc Sheet
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isPayoutFetching && (
              <TableRow>
                <TableCell colSpan={6} sx={{ textAlign: "center", py: 4 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            )}
            {!isPayoutFetching && !canFetch && (
              <TableRow>
                <TableCell colSpan={6} sx={{ textAlign: "center", py: 4, color: "#8A94A6" }}>
                  Select a {companyType === "GP" ? "company or team" : "team"} to view payout runs.
                </TableCell>
              </TableRow>
            )}
            {!isPayoutFetching && canFetch && payoutRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} sx={{ textAlign: "center", py: 4, color: "#8A94A6" }}>
                  No payout runs for the selected team(s).
                </TableCell>
              </TableRow>
            )}
            {!isPayoutFetching &&
              canFetch &&
              payoutRows.map((row) => {
                const isExpanded = payoutExpandedRows.includes(row.id);

                return (
                  <React.Fragment key={row.id}>
                    <TableRow hover>
                      <TableCell sx={payoutParentCellSx}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                          <IconButton
                            size="small"
                            onClick={() => togglePayoutExpand(row.id)}
                            disabled={!row.details?.length}
                            sx={{ color: "#97A0AD" }}
                          >
                            {isExpanded ? (
                              <KeyboardArrowDownRoundedIcon fontSize="small" />
                            ) : (
                              <KeyboardArrowRightRoundedIcon fontSize="small" />
                            )}
                          </IconButton>
                          <Box
                            sx={{
                              px: 1.2,
                              py: 0.4,
                              borderRadius: "8px",
                              bgcolor: "#EBF3FF",
                              color: "#2C6AF2",
                              fontSize: "13px",
                              fontWeight: 700,
                              lineHeight: 1,
                              minWidth: "56px",
                              textAlign: "center",
                            }}
                          >
                            {displayBiCode(row.id)}
                          </Box>
                          <Typography sx={{ fontWeight: 700, color: "#2E3847", fontSize: "14px" }}>
                            {row.cpo}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={payoutParentCellSx}>
                        <Box
                          sx={{
                            display: "inline-flex",
                            px: 1,
                            py: 0.3,
                            borderRadius: "8px",
                            bgcolor: "#EFF3F7",
                            color: "#677487",
                            fontWeight: 700,
                            fontSize: "12px",
                          }}
                        >
                          {row.source}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ ...payoutParentCellSx, whiteSpace: "nowrap", color: "#5A6576" }}>
                        {row.period}
                      </TableCell>
                      <TableCell sx={{ ...payoutParentCellSx, textAlign: "right", color: "#1A9B52", fontWeight: 700 }}>
                        {row.total}
                      </TableCell>
                      <TableCell sx={{ ...payoutParentCellSx, textAlign: "right", color: "#536174" }}>
                        {row.payees}
                      </TableCell>
                      <TableCell sx={{ ...payoutParentCellSx, textAlign: "center", color: "#8B5CF6" }}>
                        <AttachFileRoundedIcon fontSize="small" />
                      </TableCell>
                    </TableRow>
                    {isExpanded && row.details?.length > 0 && (
                      <TableRow>
                        <TableCell colSpan={6} sx={{ p: 0, borderBottom: "1px solid #E8EBF0" }}>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ ...payoutChildHeaderCellSx, pl: 7 }}>
                                  Payout Date
                                </TableCell>
                                <TableCell sx={payoutChildHeaderCellSx}>Beneficiary</TableCell>
                                <TableCell sx={payoutChildHeaderCellSx}>Mode</TableCell>
                                <TableCell sx={payoutChildHeaderCellSx}>Account / IFSC</TableCell>
                                <TableCell sx={{ ...payoutChildHeaderCellSx, textAlign: "right" }}>
                                  Amount
                                </TableCell>
                                <TableCell sx={{ ...payoutChildHeaderCellSx, textAlign: "center" }}>
                                  Commission Invoice
                                </TableCell>
                                <TableCell sx={{ ...payoutChildHeaderCellSx, textAlign: "center" }}>
                                  Actions
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {row.details.map((detail, index) => (
                                <TableRow key={`${row.id}-${index}`}>
                                  <TableCell sx={{ ...payoutChildCellSx, pl: 7 }}>{detail.payoutDate}</TableCell>
                                  <TableCell sx={payoutChildCellSx}>{detail.beneficiary}</TableCell>
                                  <TableCell sx={payoutChildCellSx}>{detail.mode}</TableCell>
                                  <TableCell sx={payoutChildCellSx}>{detail.accountIfsc}</TableCell>
                                  <TableCell sx={{ ...payoutChildCellSx, textAlign: "right", fontWeight: 700 }}>
                                    {detail.amount}
                                  </TableCell>
                                  <TableCell sx={{ ...payoutChildCellSx, textAlign: "center" }}>
                                    <Tooltip title="Download Zynka commission invoice (PDF)">
                                      <span>
                                        <IconButton
                                          size="small"
                                          sx={{ color: "#8B5CF6" }}
                                          disabled={invoiceLoadingId === detail.payoutId}
                                          onClick={() => handleDownloadCommissionInvoice(detail.payoutId)}
                                        >
                                          {invoiceLoadingId === detail.payoutId ? (
                                            <CircularProgress size={16} color="inherit" />
                                          ) : (
                                            <ReceiptLongOutlinedIcon fontSize="small" />
                                          )}
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                  </TableCell>
                                  <TableCell sx={{ ...payoutChildCellSx, textAlign: "center" }}>
                                    <Tooltip title="Download this payout's statement">
                                      <span>
                                        <IconButton
                                          size="small"
                                          sx={{ color: "#10A368" }}
                                          disabled={downloadingId === detail.payoutId}
                                          onClick={() => handleDownloadPayoutStatement(detail.payoutId)}
                                        >
                                          {downloadingId === detail.payoutId ? (
                                            <CircularProgress size={16} color="inherit" />
                                          ) : (
                                            <FileDownloadOutlinedIcon fontSize="small" />
                                          )}
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                    <Tooltip title="Email this payout's statement">
                                      <IconButton
                                        size="small"
                                        sx={{ color: "#4D6FFF" }}
                                        onClick={() => openEmailForPayout(row, detail)}
                                      >
                                        <MailOutlineRoundedIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>

      <EmailPayoutStatementDialog
        open={emailDialogOpen}
        onClose={() => setEmailDialogOpen(false)}
        selectedRuns={emailRuns}
        onSend={handleSendPayoutStatement}
        onBuildPreview={handleBuildStatementPreview}
      />

      {/* Hidden template used to rasterize the commission invoice HTML into a PDF.
          Width matches the invoice shell (640px) + its 16px side padding so the
          full invoice is captured edge-to-edge and nothing clips off the right. */}
      <div style={{ position: "fixed", left: "-10000px", top: 0, width: "672px", backgroundColor: "#ffffff" }}>
        <InvoiceTemplate ref={invoiceRef} transactionData={commissionInvoiceHtml} />
      </div>
    </Card>
  );
};

export default PayoutReportTab;
